// src/components/.../TeacherCourse/Config.jsx
import { toEnglishDigits, toPersianDigits } from "../../../../utils/convertNumbers";
import { isFarsiInput } from "../../../../utils/validation";

export function buildTeacherCourseConfig({
  mode = "create",
  record = null,
  selectedTeacherUser = "",
  onAfterSubmit,
  feeOnly = false,
} = {}) {
  const toS = (v) => (v === null || v === undefined ? "" : String(v));
  const isEdit = mode === "edit" && record?.id;

  const numberish = [
    "capacity",
    "fee",
    "duration",
    "registration_sessions",
    "teacher_percentage",
    "fixed_course_salary",
  ];

  const toNumberOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/\s|,/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const cfg = {
    submitUrl: isEdit
      ? `/operations/api/v1/teachers-and-courses/${record.id}/`
      : "/operations/api/v1/teachers-and-courses/",
    submitMethod: isEdit ? "PATCH" : "POST",

    // Validation configuration for Persian-only fields
    farsiOnlyFields: ["course_name"],
    isFarsiInput: isFarsiInput,

    /**
     * Normalize/prune before submit to satisfy DRF:
     * - force capacity=1 for individual
     * - coerce numeric-ish fields to numbers (or drop if empty)
     * - drop empty strings to avoid "" hitting IntegerField
     */
    transformBeforeSubmit: (raw) => {
      // Pricing-only edit (course has active enrollments): submit exactly the keys the
      // backend allowlist accepts (fee, teacher_percentage, fixed_course_salary) and
      // nothing else, so the request passes the guard. The pay-type radio is UI-only
      // and resolves to "the other field is 0", same as a full edit.
      if (feeOnly) {
        const out = {};
        const feeVal = toNumberOrNull(toEnglishDigits(String(raw.fee ?? "")));
        if (feeVal !== null) out.fee = feeVal;
        const payType = raw.course_payment_type || "percentage";
        if (payType === "fixed") {
          out.teacher_percentage = 0;
          const fixedVal = toNumberOrNull(toEnglishDigits(String(raw.fixed_course_salary ?? "")));
          if (fixedVal !== null) out.fixed_course_salary = fixedVal;
        } else {
          out.fixed_course_salary = 0;
          const pctVal = toNumberOrNull(toEnglishDigits(String(raw.teacher_percentage ?? "")));
          if (pctVal !== null) out.teacher_percentage = pctVal;
        }
        return out;
      }

      const out = { ...raw };

      // Defensive: if teacher is still an object, extract the ID
      if (out.teacher && typeof out.teacher === 'object' && out.teacher.id) {
        out.teacher = out.teacher.id;
      }

      // Trim strings and ensure English digits (final safety belt)
      for (const k of Object.keys(out)) {
        if (typeof out[k] === "string") {
          out[k] = toEnglishDigits(out[k].trim());
        }
      }

      // Coerce teacher to number if possible (backend expects user id as int)
      if (out.teacher !== undefined && out.teacher !== null && out.teacher !== "") {
        const tnum = Number(out.teacher);
        if (!Number.isNaN(tnum)) {
          out.teacher = tnum;
        }
      }

      // Numbers → number/null
      for (const k of numberish) {
        if (k in out) out[k] = toNumberOrNull(out[k]);
      }

      // Course-type specific rules
      if ((out.course_type || "individual") === "individual") {
        // Critical: explicitly send capacity=1 on PATCH to override old values
        out.capacity = 1;
      } else {
        // Group: if capacity is "", remove it; backend will validate >= 2
        if (out.capacity === null) {
          // if UI left it blank and we converted to null, we can omit it on PATCH
          delete out.capacity;
        }
      }

      // Handle payment type: set opposite field to 0
      // course_payment_type is UI-only, don't send it to backend
      if (out.course_payment_type) {
        if (out.course_payment_type === "percentage") {
          // Set fixed_course_salary to 0 when percentage is selected
          out.fixed_course_salary = 0;
        } else if (out.course_payment_type === "fixed") {
          // Set teacher_percentage to 0 when fixed payment is selected
          // (it's required in backend but not used for fixed payment calculations)
          out.teacher_percentage = 0;
          // Ensure fixed_course_salary is set when fixed is selected
          // (validation will catch if it's empty)
        }
        // Remove UI-only field
        delete out.course_payment_type;
      }

      // Remove empty strings entirely so we don't send "" to DRF
      for (const [k, v] of Object.entries(out)) {
        if (v === "") delete out[k];
      }

      return out;
    },

    postSubmit: (params) => {
      const { resetForm, setSnackbar } = params || {};
      
      try {
        if (isEdit) {
          if (typeof setSnackbar === 'function') {
            setSnackbar("دوره با موفقیت ویرایش شد.");
          }
        } else {
          // Show success message
          if (typeof setSnackbar === 'function') {
            setSnackbar("دوره با موفقیت ثبت شد.");
          }
          // Reset form to initial state
          if (typeof resetForm === 'function') {
            resetForm();
          }
        }
        onAfterSubmit?.(isEdit ? "edit" : "create");
      } catch (error) {
        console.error("Error in postSubmit:", error);
      }
    },

    // Keep capacity in state only for group editing UX; submit logic still enforces correctness
    initialState: isEdit
      ? {
          course_name: toS(record?.course_name),
          course_type: record?.course_type || "individual",
          capacity:
            (record?.course_type === "group" ? toS(record?.capacity ?? "") : ""),
          teacher: (() => {
            let teacherId = null;
            if (record?.teacher && typeof record.teacher === 'object' && record.teacher.id) {
              teacherId = record.teacher.id;
            } else if (record?.teacher) {
              teacherId = record.teacher;
            } else if (selectedTeacherUser) {
              teacherId = selectedTeacherUser;
            }
            return teacherId || "";
          })(),
          fee: toS(record?.fee),
          duration: toS(record?.duration),
          registration_sessions: toS(record?.registration_sessions),
          // Determine payment type based on whether fixed_course_salary exists
          course_payment_type: (record?.fixed_course_salary && record.fixed_course_salary > 0) ? "fixed" : "percentage",
          teacher_percentage: toS(record?.teacher_percentage),
          fixed_course_salary: toS(record?.fixed_course_salary || ""),
        }
      : {
          course_name: "",
          course_type: "individual",
          capacity: "", // hidden by condition unless "group"
          teacher: toS(selectedTeacherUser || ""),
          fee: "",
          duration: "",
          registration_sessions: "",
          course_payment_type: "percentage", // Default to percentage
          teacher_percentage: "",
          fixed_course_salary: "",
        },

    fields: {
      teacher: {
        type: "searchable",
        label: "آموزگار",
        required: true,
        apiUrl: "/accounts/api/v1/profile/teachers/",
        placeholder: "جستجوی آموزگار...",
        getOptionLabel: (option) => {
          if (!option) return '';
          const user = option?.user;
          if (!user) return '';
          const first = user.first_name?.trim() || '';
          const last = user.last_name?.trim() || '';
          const full = `${first} ${last}`.trim();
          return full || `آموزگار #${option.id}`;
        },
        getOptionValue: (option) => option?.user?.id,
        // Provide an initialOption so the autocomplete can show label on edit
        // Shape must match TeacherProfile option: { id, user: { id, first_name, last_name, ... } }
        initialOption: (() => {
          if (isEdit && record?.teacher && typeof record.teacher === 'object') {
            const u = record.teacher; // { id, first_name, last_name, ... }
            return {
              id: u.id,
              user: {
                id: u.id,
                first_name: u.first_name,
                last_name: u.last_name,
                email: u.email,
              },
            };
          }
          return null;
        })(),
      },
      course_name: { 
        type: "text", 
        label: "عنوان دوره", 
        required: true
      },
      course_type: {
        type: "radio",
        label: "نوع دوره",
        required: true,
        inline: true,
        options: [
          { value: "individual", label: "خصوصی" },
          { value: "group", label: "گروهی" },
        ],
      },
      capacity: {
        type: "number",
        label: "تعداد هنرجو (فقط برای گروهی)",
        min: 2,
        step: 1,
        required: false,
        condition: (fd) => (fd?.course_type || "individual") === "group",
        helperText: "حداقل ۲ نفر.",
        digits: "fa-ui/en-wire", // Persian display, English storage
      },
      fee: {
        type: "text",
        label: "شهریه (تومان)",
        required: true,
        digits: "fa-ui/en-wire", // Persian display, English storage
      },
      duration: { 
        type: "select", 
        label: "مدت کلاس (دقیقه)", 
        required: true,
        options: [30, 45, 60, 90, 120, 150, 180].map((d) => ({
          value: String(d),
          label: `${toPersianDigits(d)} دقیقه`,
        })),
      },
      registration_sessions: { 
        type: "text", 
        label: "تعداد جلسات", 
        required: true,
        digits: "fa-ui/en-wire", // Persian display, English storage
      },
      course_payment_type: {
        type: "radio",
        label: "نحوه پرداخت حق‌الزحمه",
        required: true,
        inline: true,
        options: [
          { value: "percentage", label: "درصد حق‌الزحمه" },
          { value: "fixed", label: "پرداخت مشخص برای این دوره" },
        ],
      },
      teacher_percentage: { 
        type: "text", 
        label: "درصد حق‌الزحمه", 
        required: (fd) => (fd?.course_payment_type || "percentage") === "percentage",
        digits: "fa-ui/en-wire", // Persian display, English storage
        condition: (fd) => (fd?.course_payment_type || "percentage") === "percentage",
      },
      fixed_course_salary: {
        type: "text",
        label: "حقوق ثابت دوره (تومان)",
        required: (fd) => (fd?.course_payment_type || "percentage") === "fixed",
        helperText: "مبلغ ثابت برای کل دوره (یک‌بار پرداخت در پایان ماه).",
        digits: "fa-ui/en-wire", // Persian display, English storage
        condition: (fd) => (fd?.course_payment_type || "percentage") === "fixed",
      },
    },
  };

  // Pricing-only edit mode (active enrollments): fee and the teacher pay scheme stay
  // editable; every other field is locked. Notes explain the forward-only effect.
  if (feeOnly) {
    const editable = new Set(["fee", "course_payment_type", "teacher_percentage", "fixed_course_salary"]);
    for (const key of Object.keys(cfg.fields)) {
      if (!editable.has(key)) {
        cfg.fields[key] = { ...cfg.fields[key], disabled: true };
      }
    }
    cfg.fields.fee = {
      ...cfg.fields.fee,
      helperText:
        "شهریه جدید فقط برای ثبت‌نام‌های بعدی اعمال می‌شود؛ هنرجویان فعلی با همان شهریه قبلی ادامه می‌دهند.",
    };
    const payNote = "از جلسه‌های بعدی برای همه هنرجویان اعمال می‌شود؛ جلسه‌های ثبت‌شده تغییر نمی‌کنند.";
    cfg.fields.teacher_percentage = { ...cfg.fields.teacher_percentage, helperText: payNote };
    cfg.fields.fixed_course_salary = { ...cfg.fields.fixed_course_salary, helperText: payNote };
  }

  return cfg;
}
